<template>
  <container>
    <!-- Header Area -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Scheduled Runs</h1>
        <p class="page-subtitle">Total: {{ totalElements }} runs</p>
      </div>

      <div class="search-controls">
        <div class="control-group">
          <label class="control-label">Search Run</label>
          <input type="text" v-model="filters.query" placeholder="Run name or ID..." @keyup.enter="performSearch" class="control-input">
        </div>

        <div class="control-group">
          <label class="control-label">Trainer</label>
          <select v-model="filters.trainerId" @change="performSearch" class="control-select">
              <option value="">All Trainers</option>
              <option v-for="t in allTrainers" :key="t.id" :value="t.id">
                  {{ t.firstName }} {{ t.lastName }}
              </option>
          </select>
        </div>

        <div class="control-group">
          <label class="control-label">Date</label>
          <input type="date" v-model="filters.date" @change="performSearch" class="control-input">
        </div>

        <div class="control-group" style="display: flex; align-items: flex-end;">
          <button @click="performSearch" class="btn btn--primary" style="height: 34px;">Search</button>
        </div>
      </div>
    </div>

    <race-table-list :races="racesScheduled" message-empty-list="There are no scheduled runs" type="resume"></race-table-list>

    <!-- Pagination -->
    <div v-if="totalElements > 0" class="pagination-container">
      <paginate
        v-model="filters.pageNo"
        :page-count="Math.ceil(totalElements / filters.pageSize)"
        :page-range="7"
        :margin-pages="2"
        :click-handler="changePageHandler"
        :prev-text="'Prev'"
        :next-text="'Next'"
        :container-class="'pagination custom-pagination'"
        :page-class="'page-item'">
      </paginate>
    </div>
  </container>
</template>

<script>
import Container from '@/components/Container'
import RaceTableList from '@/components/Race/RaceTableList'
import { mapActions, mapGetters } from 'vuex'
import racesByTrainerService from '@/services/racesByTrainer'
import Paginate from 'vuejs-paginate'

export default {
  name: 'scheduled-runs',
  data () {
    return {
      racesScheduled: [],
      totalElements: 0,
      filters: {
        query: '',
        trainerId: '',
        date: '',
        pageNo: 1,
        pageSize: 25
      }
    }
  },
  computed: {
    ...mapGetters({
      allTrainers: 'trainers/allTrainers'
    })
  },
  methods: {
    initialize () {
      this.fetchAllTrainersList()
      this.fetchRuns()
    },
    fetchRuns () {
      this.addTaskToQueue('fetchScheduledRuns')

      var params = {
        pageNo: this.filters.pageNo,
        pageSize: this.filters.pageSize,
        query: this.filters.query,
        trainerId: this.filters.trainerId,
        date: this.filters.date,
        filter: 'future'
      }

      racesByTrainerService.getAllRuns(params)
        .then(function (response) {
          this.totalElements = response.body.totalElements || 0
          // For scheduled runs startDate is null; use date as the start time source
          // so RaceTableList can format h:mm A correctly from race.date
          var runs = response.body.content || []
          this.racesScheduled = runs.map(function (run) {
            if (!run.startDate) {
              return Object.assign({}, run, { startDate: run.date })
            }
            return run
          })
        }.bind(this))
        .catch(function (err) { console.error(err) })
        .finally(function () {
          this.removeTaskFromQueue('fetchScheduledRuns')
        }.bind(this))
    },
    performSearch () {
      this.filters.pageNo = 1
      this.fetchRuns()
    },
    changePageHandler (pageNum) {
      this.filters.pageNo = pageNum
      this.fetchRuns()
    },
    ...mapActions({
      fetchAllTrainersList: 'trainers/fetchAllTrainersList',
      addTaskToQueue: 'LoadingQueue/addTaskToQueue',
      removeTaskFromQueue: 'LoadingQueue/removeTaskFromQueue'
    })
  },
  created () {
    this.initialize()
  },
  components: {
    Container,
    RaceTableList,
    Paginate
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #eee;
  flex-wrap: wrap;
  gap: 15px;
}

.page-title {
  font-size: 25px;
  margin: 0;
  color: #423799;
}

.page-subtitle {
  font-size: 14px;
  color: #666;
  margin: 5px 0 0 0;
}

.search-controls {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  align-items: flex-end;
}

.control-group {
  display: flex;
  flex-direction: column;
}

.control-label {
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.control-input, .control-select {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  font-size: 14px;
  color: #333;
  background: #f9f9f9;
  transition: border-color 0.3s ease;
  height: 34px;
  box-sizing: border-box;
}

.control-input:focus, .control-select:focus {
  outline: none;
  border-color: #423799;
  background: #fff;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: background-color 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.btn--primary {
  background: #423799;
  color: white;
}

.btn--primary:hover:not(:disabled) {
  background: #352c7a;
}

.pagination-container {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}

::v-deep .pagination {
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  gap: 5px;
}

::v-deep .pagination li {
  display: inline-block;
}

::v-deep .pagination li a {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 10px;
  border-radius: 4px;
  border: 1px solid #ddd;
  background: #f9f9f9 !important;
  color: #000 !important;
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

::v-deep .pagination li a:hover {
  background: #e0e0e0 !important;
  border-color: #ccc;
}

::v-deep .pagination .active,
::v-deep .pagination li.active,
::v-deep .pagination li.active a,
::v-deep .pagination li.active span {
  background: rgba(66, 55, 153, .85) !important;
  background-color: rgba(66, 55, 153, .85) !important;
  color: #fff !important;
  border-color: rgba(66, 55, 153, .85) !important;
}

::v-deep li.page-item.active a,
::v-deep .pagination li.page-item.active a {
  color: #fff !important;
}

::v-deep .pagination li.disabled a {
  color: #aaa;
  cursor: not-allowed;
  background: #f5f5f5;
  border-color: #eee;
}
</style>



// WEBPACK FOOTER //
// ScheduledRuns.vue?442ae96e